import React, { useState, useEffect } from "react";
import { SlidersHorizontal } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { FILTER_TAGS, TAGS, BEST_MOMENTS, priceSymbol } from "@/constants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";

export default function FilterSheet({ filters, onApply }) {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  useEffect(() => setDraft(filters), [filters, open]);

  const toggleTag = (tag) => {
    const tags = draft.tags || [];
    setDraft({ ...draft, tags: tags.includes(tag) ? tags.filter((x) => x !== tag) : [...tags, tag] });
  };

  const activeCount =
    (filters.tags?.length || 0) +
    (filters.price_max ? 1 : 0) +
    (filters.open_now ? 1 : 0) +
    (filters.best_moment ? 1 : 0) +
    (filters.sort === "distance" ? 1 : 0);

  const apply = () => { onApply(draft); setOpen(false); };
  const reset = () => {
    const cleared = { ...draft, tags: [], price_max: 0, open_now: false, best_moment: "", sort: "" };
    setDraft(cleared);
    onApply(cleared);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="open-filters-btn"
          className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm hover:border-terracotta"
        >
          <SlidersHorizontal className="h-4 w-4" /> {t("filters")}
          {activeCount > 0 && (
            <span className="grid h-5 w-5 place-items-center rounded-full bg-terracotta text-[11px] text-white">{activeCount}</span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto" data-testid="filter-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{t("filters")}</SheetTitle>
          <SheetDescription className="sr-only">{t("filters")}</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-4">
          <div>
            <p className="mb-2 text-sm font-semibold text-ink">{t("budget")}</p>
            <div className="flex gap-2">
              {[0, 1, 2, 3].map((p) => (
                <button
                  key={p}
                  data-testid={`filter-budget-${p}`}
                  onClick={() => setDraft({ ...draft, price_max: p })}
                  className={`flex-1 rounded-xl border py-2.5 text-sm font-bold transition-colors ${
                    (draft.price_max || 0) === p ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-ink"
                  }`}
                >
                  {p === 0 ? t("any") : priceSymbol(p)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3">
            <span className="text-sm font-semibold text-ink">{t("open_now")}</span>
            <button
              data-testid="filter-open-now"
              onClick={() => setDraft({ ...draft, open_now: !draft.open_now })}
              className={`relative h-6 w-11 rounded-full transition-colors ${draft.open_now ? "bg-emerald-500" : "bg-stone-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${draft.open_now ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3">
            <span className="text-sm font-semibold text-ink">{t("near_me")}</span>
            <button
              data-testid="filter-near-me"
              onClick={() => setDraft({ ...draft, sort: draft.sort === "distance" ? "" : "distance" })}
              className={`relative h-6 w-11 rounded-full transition-colors ${draft.sort === "distance" ? "bg-sea" : "bg-stone-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${draft.sort === "distance" ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-ink">{t("moment")}</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setDraft({ ...draft, best_moment: "" })}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold ${!draft.best_moment ? "border-sea bg-sea text-white" : "border-border bg-white text-ink"}`}
              >{t("any")}</button>
              {Object.keys(BEST_MOMENTS).map((m) => (
                <button
                  key={m}
                  data-testid={`filter-moment-${m}`}
                  onClick={() => setDraft({ ...draft, best_moment: draft.best_moment === m ? "" : m })}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold ${draft.best_moment === m ? "border-sea bg-sea text-white" : "border-border bg-white text-ink"}`}
                >{localized(BEST_MOMENTS[m], lang)}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-ink">{t("ambiance")}</p>
            <div className="flex flex-wrap gap-2">
              {FILTER_TAGS.map((tag) => {
                const active = (draft.tags || []).includes(tag);
                return (
                  <button
                    key={tag}
                    data-testid={`filter-tag-${tag}`}
                    onClick={() => toggleTag(tag)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                      active ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border bg-white text-ink"
                    }`}
                  >{localized(TAGS[tag], lang)}</button>
                );
              })}
            </div>
          </div>
        </div>

        <SheetFooter className="flex-row gap-3">
          <button data-testid="reset-filters-btn" onClick={reset} className="flex-1 rounded-xl border border-border bg-white py-3 text-sm font-semibold text-ink">{t("reset")}</button>
          <button data-testid="apply-filters-btn" onClick={apply} className="flex-1 rounded-xl bg-terracotta py-3 text-sm font-bold text-white hover:bg-terracotta-hover">{t("apply")}</button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
